import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Plus, TriangleAlert, Trash2 } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { apiError } from '../lib';
import {
  CONTENT_LOCALES,
  appendListItem,
  blankPaths,
  changedPaths,
  fieldLabel,
  looksLikeAPrice,
  pricePaths,
  readString,
  removeListItem,
  writePath,
  type ContentDocument,
  type ContentLocale,
  type ContentNode,
  type SiteContentState,
} from './lib';

/**
 * tarabeeza.com's own words, editable here.
 *
 * The marketing site used to carry its two dictionaries as source files, so
 * changing a sentence meant a pull request and a deploy — which is also why it
 * quoted no price: nobody wanted a number frozen into a repository. The price
 * is now whatever is typed on this screen.
 *
 * THREE THINGS THIS SCREEN OWES THE SITE, and the reasons are not decorative:
 *
 *   1. BOTH LANGUAGES SIDE BY SIDE, field for field. A translator has to see
 *      the English sentence to write the Arabic one, and the site renders the
 *      two independently — there is no fallback from Arabic to English, by
 *      design — so a field written in one language and not the other is a hole
 *      on the page rather than a mixed-language page.
 *   2. PUBLISH IS BLOCKED WHILE ANY FIELD IS EMPTY, in either language, and the
 *      empty ones are listed by name. That is rule 1 made unavoidable.
 *   3. A PRICE IS WARNED ABOUT, NEVER REFUSED. The site's FAQ answers "what
 *      does it cost" with "pricing is quoted for the venue"; publishing a
 *      figure alongside it makes the page contradict itself on the question a
 *      buyer asks first. Worth saying out loud. Not worth overruling — it is
 *      their platform and their price.
 *
 * BOTH LANGUAGES PUBLISH TOGETHER, under one version, because the API refuses
 * to do it any other way. See `SiteContentService.publish()`.
 */

/**
 * Roughly how many characters fit on one line of the field at the widths this
 * page uses. The box is sized from what is in it, because the shortest thing on
 * this screen is a two-word button label and the longest is a six-hundred
 * character answer, and one fixed height cannot serve both — a heading in an
 * eight-row box is as hard to read as an answer in a one-row one.
 */
const CHARS_PER_ROW = 80;
const MAX_ROWS = 10;

function rowsFor(value: string): number {
  return Math.min(MAX_ROWS, Math.max(1, Math.ceil(value.length / CHARS_PER_ROW)));
}

export default function Content() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canManage = can('platform.manage_content');

  const [state, setState] = useState<SiteContentState | null>(null);
  const [drafts, setDrafts] = useState<Record<ContentLocale, ContentDocument> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<SiteContentState>('/site-content')
      .then(({ data }) => {
        setState(data);
        setDrafts({ en: data.en.draft, ar: data.ar.draft });
      })
      .catch((e) => setError(apiError(e, t('platform.content.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(load, [load]);

  /*
   * The English draft drives the structure of the editor — the two documents
   * have the same shape, and one of them has to be the one the rows are read
   * off. The Arabic value for each row is looked up by the same path, so a
   * field the Arabic document has never had renders as an empty box that
   * blocks publishing, which is exactly what it should do.
   */
  const sections = useMemo(() => (drafts ? Object.keys(drafts.en) : []), [drafts]);

  const blanks = useMemo(
    () =>
      drafts
        ? { en: blankPaths(drafts.en), ar: blankPaths(drafts.ar) }
        : { en: [], ar: [] },
    [drafts],
  );

  const prices = useMemo(
    () =>
      drafts
        ? { en: pricePaths(drafts.en), ar: pricePaths(drafts.ar) }
        : { en: [], ar: [] },
    [drafts],
  );

  const changed = useMemo(
    () =>
      drafts && state
        ? {
            en: changedPaths(drafts.en, state.en.published?.document),
            ar: changedPaths(drafts.ar, state.ar.published?.document),
          }
        : { en: [], ar: [] },
    [drafts, state],
  );

  const incomplete = blanks.en.length + blanks.ar.length > 0;
  const nothingToPublish = changed.en.length + changed.ar.length === 0;

  const edit = (locale: ContentLocale, path: string, value: string) =>
    setDrafts((current) =>
      current ? { ...current, [locale]: writePath(current[locale], path, value) } : current,
    );

  /* A list gains or loses an item in BOTH languages at once. Letting them drift
     apart would put an eighth question on one page and a seventh on the other. */
  const editList = (change: (document: ContentDocument) => ContentDocument) =>
    setDrafts((current) =>
      current ? { en: change(current.en), ar: change(current.ar) } : current,
    );

  const saveDrafts = () =>
    Promise.all(
      CONTENT_LOCALES.map((locale) =>
        api.put(`/site-content/draft/${locale}`, { document: drafts?.[locale] }),
      ),
    );

  const save = async () => {
    setSaving(true);
    try {
      await saveDrafts();
      toast.success(t('platform.content.saved'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.content.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    try {
      /*
       * Saved first, so Publish always publishes what is on the screen. The API
       * publishes the STORED drafts — it has no other honest source — and an
       * unsaved edit going live one press later is the kind of surprise that
       * makes people stop trusting a console.
       */
      await saveDrafts();
      const { data } = await api.post<SiteContentState>('/site-content/publish', {});
      setState(data);
      setDrafts({ en: data.en.draft, ar: data.ar.draft });
      setConfirmPublish(false);
      toast.success(t('platform.content.published', { version: data.en.published?.version ?? 1 }));
    } catch (e) {
      toast.error(apiError(e, t('platform.content.publishFailed')));
    } finally {
      setSaving(false);
    }
  };

  const discard = async () => {
    setSaving(true);
    try {
      await Promise.all(
        CONTENT_LOCALES.map((locale) => api.delete(`/site-content/draft/${locale}`)),
      );
      toast.success(t('platform.content.discarded'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.content.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!state || !drafts) return <EmptyState title={t('platform.content.loadFailed')} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">{t('platform.content.title')}</h1>
        <p className="text-sm text-[var(--muted-foreground)]">{t('platform.content.subtitle')}</p>
      </div>

      {!canManage && (
        <p className="rounded-md border border-[var(--border)] bg-[var(--muted)] p-3 text-sm text-[var(--muted-foreground)]">
          {t('platform.content.readOnly')}
        </p>
      )}

      {/* WHAT IS LIVE, AND WHAT IS DRAFTED — the first question anyone opening
          this screen has, so it is answered before the editor starts. */}
      <div className="grid gap-4 sm:grid-cols-2">
        {CONTENT_LOCALES.map((locale) => {
          const live = state[locale].published;
          return (
            <Card key={locale}>
              <CardHeader className="flex-row items-center justify-between gap-2">
                <CardTitle>{t(`platform.content.locale.${locale}`)}</CardTitle>
                {changed[locale].length > 0 ? (
                  <Badge tone="warning">
                    {t('platform.content.changedCount', { n: changed[locale].length })}
                  </Badge>
                ) : (
                  <Badge tone="success">{t('platform.content.matchesLive')}</Badge>
                )}
              </CardHeader>
              <CardContent className="space-y-1 text-sm text-[var(--muted-foreground)]">
                {live ? (
                  <>
                    <div>
                      <span className="font-medium text-[var(--foreground)]">
                        {t('platform.content.liveVersion', { version: live.version })}
                      </span>
                    </div>
                    <div>
                      {t('platform.content.publishedAt', {
                        when: formatDateTime(live.publishedAt, i18n.language),
                        who: live.publishedBy ?? '—',
                      })}
                    </div>
                  </>
                ) : (
                  /* Nothing published: the site is rendering the copy compiled
                     into its own build, which is also this editor's starting
                     point. Saying so is more useful than saying "none". */
                  <div>{t('platform.content.neverPublished')}</div>
                )}
                {state[locale].draftUpdatedAt && (
                  <div>
                    {t('platform.content.draftSaved', {
                      when: formatDateTime(state[locale].draftUpdatedAt!, i18n.language),
                      who: state[locale].draftUpdatedBy ?? '—',
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Publishing is blocked while anything is empty, and the empty fields are
          named. A count would tell a translator nothing they could act on. */}
      {incomplete && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-red-900">
          <p className="text-sm font-semibold">{t('platform.content.incompleteTitle')}</p>
          <p className="mt-1 text-xs">{t('platform.content.incompleteBody')}</p>
          {CONTENT_LOCALES.filter((locale) => blanks[locale].length > 0).map((locale) => (
            <p key={locale} className="mt-2 text-xs">
              <span className="font-medium">{t(`platform.content.locale.${locale}`)}: </span>
              <code className="break-all" dir="ltr">
                {blanks[locale].join(' · ')}
              </code>
            </p>
          ))}
        </div>
      )}

      {/* A warning, not a block. See the note at the top of this file. */}
      {prices.en.length + prices.ar.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="text-sm font-semibold">{t('platform.content.priceTitle')}</p>
            <p className="mt-1 text-xs">{t('platform.content.priceBody')}</p>
            {CONTENT_LOCALES.filter((locale) => prices[locale].length > 0).map((locale) => (
              <p key={locale} className="mt-2 text-xs">
                <span className="font-medium">{t(`platform.content.locale.${locale}`)}: </span>
                <code className="break-all" dir="ltr">
                  {prices[locale].join(' · ')}
                </code>
              </p>
            ))}
          </div>
        </div>
      )}

      {canManage && (
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => void save()} disabled={saving} variant="secondary">
            {t('platform.content.saveDraft')}
          </Button>
          <Button
            onClick={() => setConfirmPublish(true)}
            disabled={saving || incomplete || nothingToPublish}
            title={incomplete ? t('platform.content.incompleteTitle') : undefined}
          >
            {t('platform.content.publish')}
          </Button>
          <Button
            variant="outline"
            onClick={() => void discard()}
            disabled={saving || state.en.draftSource !== 'draft'}
          >
            {t('platform.content.discard')}
          </Button>
        </div>
      )}

      {sections.map((section) => {
        const expanded = open === section;
        const sectionChanges = CONTENT_LOCALES.reduce(
          (total, locale) =>
            total + changed[locale].filter((path) => path.startsWith(section)).length,
          0,
        );
        const sectionBlanks = CONTENT_LOCALES.reduce(
          (total, locale) =>
            total + blanks[locale].filter((path) => path.startsWith(section)).length,
          0,
        );

        return (
          <Card key={section}>
            <CardHeader className="flex-row items-center justify-between gap-2">
              <button
                type="button"
                className="flex min-w-0 items-center gap-2 text-start"
                onClick={() => setOpen(expanded ? null : section)}
                aria-expanded={expanded}
              >
                {/* An icon that points at the reading direction's "forward"
                    would need an RTL override; down-vs-right needs none. */}
                {expanded ? (
                  <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                )}
                <CardTitle className="truncate">
                  {t(`platform.content.sections.${section}`, section)}
                </CardTitle>
              </button>
              <div className="flex shrink-0 items-center gap-2">
                {sectionBlanks > 0 && (
                  <Badge tone="danger">
                    {t('platform.content.blankCount', { n: sectionBlanks })}
                  </Badge>
                )}
                {sectionChanges > 0 && (
                  <Badge tone="warning">
                    {t('platform.content.changedCount', { n: sectionChanges })}
                  </Badge>
                )}
              </div>
            </CardHeader>
            {expanded && (
              <CardContent className="space-y-5">
                <Node
                  path={section}
                  node={drafts.en[section]}
                  drafts={drafts}
                  live={state}
                  disabled={!canManage}
                  onEdit={edit}
                  onList={editList}
                />
              </CardContent>
            )}
          </Card>
        );
      })}

      <Dialog
        open={confirmPublish}
        onClose={() => setConfirmPublish(false)}
        title={t('platform.content.publish')}
        description={t('platform.content.publishHint')}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmPublish(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={() => void publish()} disabled={saving}>
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-[var(--muted-foreground)]">
          {t('platform.content.publishBody', {
            en: changed.en.length,
            ar: changed.ar.length,
          })}
        </p>
      </Dialog>
    </div>
  );
}

/**
 * One node of the document, recursively.
 *
 * The structure is read off the ENGLISH draft; the Arabic value for each field
 * is looked up by the same path. That is what puts the two languages on one
 * row, and it is also why a field the Arabic document does not have renders as
 * an empty box rather than silently disappearing.
 */
function Node({
  path,
  node,
  drafts,
  live,
  disabled,
  onEdit,
  onList,
}: {
  path: string;
  node: ContentNode | undefined;
  drafts: Record<ContentLocale, ContentDocument>;
  live: SiteContentState;
  disabled: boolean;
  onEdit: (locale: ContentLocale, path: string, value: string) => void;
  onList: (change: (document: ContentDocument) => ContentDocument) => void;
}) {
  const { t } = useTranslation();

  if (typeof node === 'string') {
    return <Row path={path} drafts={drafts} live={live} disabled={disabled} onEdit={onEdit} />;
  }

  if (Array.isArray(node)) {
    return (
      <div className="space-y-4">
        {node.map((item, index) => (
          <div
            key={`${path}[${index}]`}
            className="rounded-lg border border-[var(--border)] p-3"
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="eyebrow text-[var(--muted-foreground)]">
                {t('platform.content.item', { n: index + 1 })}
              </span>
              {!disabled && node.length > 1 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onList((document) => removeListItem(document, path, index))}
                  aria-label={t('platform.content.removeItem')}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <Node
              path={`${path}[${index}]`}
              node={item}
              drafts={drafts}
              live={live}
              disabled={disabled}
              onEdit={onEdit}
              onList={onList}
            />
          </div>
        ))}
        {!disabled && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onList((document) => appendListItem(document, path))}
          >
            <Plus className="h-4 w-4" />
            {t('platform.content.addItem')}
          </Button>
        )}
      </div>
    );
  }

  if (!node || typeof node !== 'object') return null;

  return (
    <div className="space-y-5">
      {Object.entries(node).map(([key, child]) => (
        <Node
          key={`${path}.${key}`}
          path={`${path}.${key}`}
          node={child}
          drafts={drafts}
          live={live}
          disabled={disabled}
          onEdit={onEdit}
          onList={onList}
        />
      ))}
    </div>
  );
}

/** One field, in both languages, with what the site is serving underneath. */
function Row({
  path,
  drafts,
  live,
  disabled,
  onEdit,
}: {
  path: string;
  drafts: Record<ContentLocale, ContentDocument>;
  live: SiteContentState;
  disabled: boolean;
  onEdit: (locale: ContentLocale, path: string, value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-1.5">
      <code className="block text-xs text-[var(--muted-foreground)]" dir="ltr">
        {fieldLabel(path)}
      </code>
      <div className="grid gap-3 sm:grid-cols-2">
        {CONTENT_LOCALES.map((locale) => {
          const value = readString(drafts[locale], path);
          const published = live[locale].published?.document;
          const liveValue = published ? readString(published, path) : null;
          const blank = value.trim() === '';
          const priced = looksLikeAPrice(value);

          return (
            <div key={locale} className="space-y-1">
              <label className="block text-xs font-medium text-[var(--muted-foreground)]">
                {t(`platform.content.locale.${locale}`)}
              </label>
              <Textarea
                rows={rowsFor(value)}
                dir={locale === 'ar' ? 'rtl' : 'ltr'}
                lang={locale}
                value={value}
                disabled={disabled}
                aria-invalid={blank}
                aria-label={`${fieldLabel(path)} (${locale})`}
                className={blank ? 'border-red-400' : priced ? 'border-amber-400' : undefined}
                onChange={(e) => onEdit(locale, path, e.target.value)}
              />
              {blank ? (
                <p className="text-xs text-red-600">{t('platform.content.required')}</p>
              ) : (
                liveValue !== null &&
                liveValue !== value && (
                  /* What the site is serving right now, so the difference
                     between live and drafted is visible on the field itself
                     rather than only in a count at the top of the page. */
                  <p className="text-xs text-[var(--muted-foreground)]">
                    <span className="font-medium">{t('platform.content.live')}: </span>
                    {liveValue}
                  </p>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
